import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Toggle from "common/Toggle";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { CreateSettingsGroupApi } from "services/apiService";

interface CreateSettingsGroupProps {
  open: boolean;
  handleClose: () => void;
  onActionComplete: () => void;
}

const CreateSettingsGroup: React.FC<CreateSettingsGroupProps> = ({
  open,
  handleClose,
  onActionComplete,
}) => {
  const [formdata, setFormdata] = useState({
    name: "",
    is_active: 1,
  });
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [errors, setErrors] = useState<{ name?: string }>({});

  const validateForm = () => {
    const newErrors: { name?: string } = {};

    if (!formdata.name) {
      newErrors.name = "Settings Name is required.";
    } else if (!/^[a-zA-Z0-9-]+$/.test(formdata.name)) {
      newErrors.name = "Only letters, numbers, and '-' are allowed.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "name") {
      let sanitizedValue = value.replace(/[^a-zA-Z0-9-]/g, "").slice(0, 25);
      sanitizedValue =
        sanitizedValue.charAt(0).toUpperCase() + sanitizedValue.slice(1);

      setFormdata((prev) => ({ ...prev, [name]: sanitizedValue }));
    } else {
      setFormdata((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const payload = { name: formdata.name, is_active: formdata.is_active };
      const response = await CreateSettingsGroupApi(payload);
      if (response.status_code === 200) {
        toast.success("Settings group created successfully!");
        setFormdata({ name: "", is_active: 1 });
        handleClose();
        onActionComplete();
        navigate("/setting-list");
      } else {
        toast.error("Creation failed. Please try again.");
      }
    } catch (error) {
      console.error("Error creating settings group:", error);
      toast.error("An error occurred. Please try again.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 3,
          padding: 2,
          boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "1.5rem",
          fontWeight: "bold",
          textAlign: "center",
          paddingBottom: 1,
        }}
      >
        Create Settings Group
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Box sx={{ paddingX: 2, paddingY: 1 }}>
            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Settings Group Name
              </Typography>
              <TextField
                variant="outlined"
                name="name"
                id="name"
                value={formdata.name}
                onChange={handleInputChange}
                placeholder="Enter name"
                fullWidth
                error={!!errors.name}
                helperText={
                  errors.name || "Only letters, numbers, and '-' allowed."
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{ width: "25%" }}
              >
                Status
              </Typography>
              <Toggle
                checked={formdata.is_active === 1}
                onChange={(checked) =>
                  setFormdata((prevData) => ({
                    ...prevData,
                    is_active: checked ? 1 : 0,
                  }))
                }
              />
            </Box>
          </Box>
        </form>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", paddingBottom: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          size="large"
          sx={{
            borderRadius: 2,
            paddingX: 3,
            fontWeight: "bold",
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.light,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          sx={{
            borderRadius: 2,
            paddingX: 3,
            fontWeight: "bold",
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          <FontAwesomeIcon icon={faCirclePlus} />
          &nbsp;&nbsp; Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSettingsGroup;
